import React, { useState } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Button,
} from '@fluentui/react-components';
import { 
  PersonRegular, 
  ChatRegular, 
  HeartRegular,
  ClockRegular,
} from '@fluentui/react-icons';
import PrayerCommentBox from './PrayerCommentBox';
import { PrayerItem } from '../services/sharePointService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px', // Increased gap between cards
  },
  prayerCard: {
    marginBottom: '20px', // Increased margin
    padding: '24px', // Added more padding
    minHeight: '200px', // Set minimum height for better visibility
    boxShadow: tokens.shadow4, // Enhanced shadow for better visual separation
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '12px', // Rounded corners
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
    },
  },
  prayerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px', // Increased margin
  },
  prayerInfo: {
    flex: 1,
  },
  prayerTitle: {
    fontSize: '20px', // Larger title
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
    lineHeight: '1.3',
  },
  prayerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px', // Increased gap
    marginTop: '12px',
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap', // Allow wrapping on smaller screens
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px', // Slightly larger text
    padding: '4px 8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  prayerDescription: {
    marginTop: '16px', // Increased margin
    lineHeight: '1.6', // Better line height
    fontSize: '16px', // Larger text
    color: tokens.colorNeutralForeground1,
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '80px', // Minimum height for description
  },
  priorityBadge: {
    marginLeft: '8px',
  },
  commentsSection: {
    marginTop: '20px', // Increased margin
    paddingTop: '20px',
    borderTop: `2px solid ${tokens.colorNeutralStroke2}`, // Thicker border
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px', // Increased padding
    color: tokens.colorNeutralForeground3,
    fontSize: '18px', // Larger text
  },
  expandButton: {
    marginTop: '16px', // Increased margin
    padding: '8px 16px',
    fontSize: '14px',
  },
});

interface PrayerListProps {
  prayers?: PrayerItem[];
  onPrayerUpdated?: () => void; // Callback to refresh prayers when comments are added
}

const PrayerList: React.FC<PrayerListProps> = ({ prayers = [], onPrayerUpdated }) => {
  const styles = useStyles();
  const [expandedPrayer, setExpandedPrayer] = useState<number | null>(null);
  
  console.log('🔄 PrayerList received prayers:', prayers);
  console.log('🔄 Prayers length:', prayers.length);
  if (prayers.length > 0) {
    console.log('🔄 First prayer data:', prayers[0]);
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleExpanded = (prayerId: number) => {
    setExpandedPrayer(expandedPrayer === prayerId ? null : prayerId);
  };

  if (prayers.length === 0) {
    return (
      <div className={styles.emptyState}>
        <HeartRegular />
        <Body1>No prayer requests found.</Body1>
        <Caption1>Prayer requests will appear here once submitted.</Caption1>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {prayers.map((prayer) => (
        <div key={prayer.Id} className={styles.prayerCard}>
          <div className={styles.prayerHeader}>
            <div className={styles.prayerInfo}>
              <div className={styles.prayerTitle}>{prayer.Title}</div>
            </div>
          </div>

          <div className={styles.prayerMeta}>
            <div className={styles.metaItem}>
              <PersonRegular />
              <span>{prayer.CreatedBy}</span>
            </div>
            <div className={styles.metaItem}>
              <ClockRegular />
              <span>{formatDate(prayer.Created)}</span>
            </div>
            {prayer.AssignedTo && (
              <div className={styles.metaItem}>
                <PersonRegular />
                <span>Assigned: {prayer.AssignedTo}</span>
              </div>
            )}
            <div className={styles.metaItem}>
              <ChatRegular />
              <span>{prayer.CommentCount || 0} comments</span>
            </div>
          </div>

          <div className={styles.prayerDescription}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {prayer.PrayerText}
            </div>
          </div>

          <Button
            appearance="subtle"
            onClick={() => toggleExpanded(prayer.Id)}
            className={styles.expandButton}
          >
            {expandedPrayer === prayer.Id ? 'Hide Comments' : 'View Comments'}
          </Button>

          {expandedPrayer === prayer.Id && (
            <div className={styles.commentsSection}>
              <PrayerCommentBox 
                prayerId={prayer.Id} 
                onCommentAdded={onPrayerUpdated} // Pass callback to refresh prayer list
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PrayerList; 